/**
 * Timeouts for browser network calls.
 *
 * `fetch` has no timeout of its own. Without a signal it waits on a silent peer
 * indefinitely — a spinner that never resolves and never errors, which the user
 * reads as "broken" and which no error boundary or log will ever report,
 * because nothing failed.
 *
 * Fifteen seconds is far past a healthy p99 for these endpoints; anything still
 * outstanding has failed in the only sense the person waiting cares about.
 *
 * **Streams are deliberately not covered here.** `agentStream` bounds itself
 * with a stall watchdog that aborts on silence — the right shape for a
 * response that stays open as long as the reply takes. A wall-clock timeout
 * there would sever a healthy long turn mid-token, which is a worse failure
 * than the one it prevents, so it gets inactivity-based bounding instead.
 */

/** Ceiling for an ordinary request/response API call. */
const REQUEST_TIMEOUT_MS = 15_000;

/**
 * A timeout signal built from `AbortController`, for engines that predate
 * `AbortSignal.timeout`.
 *
 * The abort reason is a `TimeoutError` `DOMException` so callers cannot tell
 * this apart from the native path. On an engine old enough to ignore
 * `abort()`'s reason argument the signal still aborts, just as an
 * `AbortError` — the request is bounded either way, which is the point.
 */
function timeoutSignal(ms: number): AbortSignal {
  const controller = new AbortController();
  setTimeout(() => {
    controller.abort(new DOMException('signal timed out', 'TimeoutError'));
  }, ms);
  return controller.signal;
}

/**
 * Abort signal for a normal API request.
 *
 * `fetch(url, { signal: requestTimeout() })`. On expiry the fetch rejects with
 * a `TimeoutError`, which callers surface as their existing friendly error —
 * a failure the user can act on instead of a spinner that never stops.
 *
 * **Why the feature test.** `AbortSignal.timeout` is Safari 16+ (MDN
 * browser-compat-data `api.AbortSignal.timeout_static`: safari 16, safari_ios
 * mirrors it; read 2026-08-22). Calling it on an older engine throws a
 * `TypeError` *before* `fetch` is reached, so the request is never issued at
 * all — and a caller that catches its own network errors swallows that throw
 * and waits forever on a request it never made.
 *
 * That is exactly what happened in prod on 2026-08-22: an iPad on Mobile
 * Safari 15.6.8 / iOS 15.8.7 sat on `/auth/check-email` indefinitely after the
 * sign-in was approved from a second device. The auth API Gateway access log
 * showed `POST /approve 200` and not one `GET /status` from the iPad. Every
 * bounded `fetch` in the app shared the defect; it was introduced with this
 * helper on 2026-08-17 and reached prod because nothing in the gate exercised
 * a WebKit engine.
 */
export function requestTimeout(ms: number = REQUEST_TIMEOUT_MS): AbortSignal {
  if (typeof AbortSignal.timeout === 'function') {
    return AbortSignal.timeout(ms);
  }
  return timeoutSignal(ms);
}
